import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    ViewChild,
    forwardRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import * as CodeMirror from 'codemirror';

import 'codemirror/mode/htmlmixed/htmlmixed';
import 'codemirror/mode/xml/xml';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/css/css';

@Component({
    standalone: false,
    selector: 'app-html-editor',
    templateUrl: './html-editor.component.html',
    styleUrls: ['./html-editor.component.css'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => HtmlEditorComponent),
        multi: true
    }]
})
export class HtmlEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
    @ViewChild('editor') editorRef: ElementRef;

    private cmInstance?: CodeMirror.EditorFromTextArea;
    private innerValue = '';
    private resizeObserver?: ResizeObserver;

    constructor(private hostElement: ElementRef<HTMLElement>) { }

    // Angular form callbacks
    private onChange: (value: any) => void = () => { };
    private onTouched: () => void = () => { };

    ngAfterViewInit() {
        // CodeMirror must be created while its container is visible. Creating
        // it inside a display:none modal produces an incomplete editor DOM.
        if (this.hostElement.nativeElement.getBoundingClientRect().width > 0) {
            this.initializeEditor();
            return;
        }

        this.resizeObserver = new ResizeObserver(entries => {
            if (entries[0].contentRect.width > 0) {
                this.initializeEditor();
                this.resizeObserver?.disconnect();
                this.resizeObserver = undefined;
            }
        });
        this.resizeObserver.observe(this.hostElement.nativeElement);
    }

    private initializeEditor(): void {
        if (this.cmInstance) {
            return;
        }

        this.cmInstance = CodeMirror.fromTextArea(this.editorRef.nativeElement, {
            mode: 'htmlmixed',
            lineNumbers: true,
            theme: 'material-darker',
            tabSize: 2,
            lineWrapping: true
        });
        this.cmInstance.setValue(this.innerValue);

        // Intercept changes to update the model
        this.cmInstance.on('change', () => {
            const value = this.cmInstance!.getValue();
            this.innerValue = value;
            this.onChange(value);
        });
    }

    // ControlValueAccessor implementations
    writeValue(value: string): void {
        this.innerValue = value || '';
        if (this.cmInstance) {
            this.cmInstance.setValue(this.innerValue);
        }
    }

    /**
     * Register a callback function to be called when the value changes.
     * @param fn Callback function to be called when the value changes
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Register a callback function to be called when the control is touched.
     * @param fn Callback function to be called when the control is touched
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Set the disabled state of the control.
     * @param isDisabled Boolean indicating whether the control should be disabled
     */
    setDisabledState?(isDisabled: boolean): void {
        if (this.cmInstance) {
            this.cmInstance.setOption('readOnly', isDisabled);
        }
    }

    /**
     * Focus the CodeMirror editor.
     * This method is useful for programmatically focusing the editor, for example, after a modal
     */
    public focusEditor(): void {
        if (!this.cmInstance && this.hostElement.nativeElement.getBoundingClientRect().width > 0) {
            this.initializeEditor();
        }
        if (this.cmInstance) {
            this.cmInstance.refresh(); // Force a refresh to ensure the editor is ready
            this.cmInstance.focus();   // Set focus to the editor
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }
}
